import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud, FileText, File as FileIcon, Loader2, CheckCircle2,
  AlertCircle, Trash2, Eye, RefreshCw,
} from "lucide-react";
import { documentsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const ACCEPT = ".pdf,.docx,.xlsx,.xls,.csv,.txt";

export default function DocumentLibrary() {
  const { projectId } = useParams();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // doc being edited
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    documentsApi.list(projectId).then(setDocs).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => {
      // keep polling while any doc is still extracting
      documentsApi.list(projectId).then((items) => {
        setDocs(items);
      }).catch(() => {});
    }, 3500);
    return () => clearInterval(pollRef.current);
  }, [projectId, load]);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await documentsApi.upload(projectId, files);
      toast.success(`${files.length} document(s) téléversé(s). Extraction en cours…`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur à l'upload");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await documentsApi.remove(projectId, id);
    toast.success("Supprimé");
    load();
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto w-full" data-testid="document-library-page">
      <div className="label-mono text-[10px] text-muted-foreground">ESPACE 1</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Bibliothèque documentaire</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Téléversez rapports FMI/BM/BAD, conventions de prêts, lois de finances.
        Un seul appel GPT-4o par document — les résultats sont mis en cache.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "mt-8 border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition",
          dragOver ? "border-gold bg-gold/5" : "border-border hover:border-gold/60 hover:bg-muted/20"
        )}
        data-testid="upload-document-zone"
      >
        <input ref={inputRef} type="file" multiple accept={ACCEPT}
               onChange={(e) => onFiles(e.target.files)} className="hidden"
               data-testid="upload-document-input" />
        <UploadCloud size={38} className="mx-auto text-marine dark:text-gold" />
        <div className="mt-3 font-serif text-lg">Glissez-déposez vos documents ici</div>
        <div className="text-xs text-muted-foreground mt-1">
          PDF · DOCX · XLSX · XLS · CSV · TXT — jusqu'à 10 fichiers, 150 Mo max.
        </div>
        {uploading && <div className="mt-4 label-mono text-[10px] text-gold flex items-center justify-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Téléversement…
        </div>}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-serif font-bold text-2xl">Documents ({docs.length})</h2>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-docs-btn">
          <RefreshCw size={14} className="mr-2" /> Rafraîchir
        </Button>
      </div>

      {loading ? (
        <div className="mt-6 label-mono text-xs text-muted-foreground">Chargement…</div>
      ) : docs.length === 0 ? (
        <div className="mt-6 border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground">
          Aucun document. Téléversez-en pour démarrer l'analyse.
        </div>
      ) : (
        <div className="mt-6 border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="docs-table">
            <thead className="bg-muted/30 label-mono text-[9px] text-muted-foreground">
              <tr>
                <th className="text-left p-3 w-[40%]">FICHIER</th>
                <th className="text-left p-3">TYPE</th>
                <th className="text-left p-3">CONFIANCE</th>
                <th className="text-left p-3">STATUT</th>
                <th className="text-right p-3">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-border hover:bg-muted/20">
                  <td className="p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {iconFor(d.filename)}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.filename}</div>
                        <div className="text-xs text-muted-foreground">
                          {(d.file_size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {d.doc_type ? (
                      <span className="label-mono text-[10px] bg-marine/10 text-marine dark:bg-gold/10 dark:text-gold px-2 py-0.5 rounded-sm">
                        {d.doc_type}
                      </span>
                    ) : "—"}
                    <div className="text-[11px] text-muted-foreground mt-1">{d.doc_type_label || ""}</div>
                  </td>
                  <td className="p-3">
                    {d.quality_score != null ? (
                      <span className={cn(
                        "font-mono tabular-nums text-sm",
                        d.quality_score >= 85 ? "text-success" : d.quality_score >= 60 ? "text-warn" : "text-alert"
                      )}>
                        {d.quality_score}%
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-xs">
                    <StatusBadge status={d.extraction_status} error={d.extraction_error} validated={d.validated} />
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(d)} disabled={!d.extracted_data}
                              data-testid={`doc-view-${d.id}`} title="Voir / corriger">
                        <Eye size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(d.id)}
                              data-testid={`doc-delete-${d.id}`} title="Supprimer">
                        <Trash2 size={14} className="text-alert" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExtractedDataDialog doc={editing} projectId={projectId} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function iconFor(filename) {
  const e = filename.split(".").pop().toLowerCase();
  const icon = ["pdf"].includes(e) ? <FileText size={18} className="text-alert" /> :
               ["docx", "doc"].includes(e) ? <FileText size={18} className="text-marine" /> :
               ["xlsx", "xls", "csv"].includes(e) ? <FileIcon size={18} className="text-success" /> :
               <FileIcon size={18} className="text-muted-foreground" />;
  return icon;
}

function StatusBadge({ status, error, validated }) {
  if (status === "extracting" || status === "pending") {
    return <span className="flex items-center gap-2 text-warn label-mono text-[10px]">
      <Loader2 size={12} className="animate-spin" /> {status === "extracting" ? "EXTRACTION…" : "EN ATTENTE"}
    </span>;
  }
  if (status === "error") {
    return <span className="flex items-center gap-2 text-alert label-mono text-[10px]" title={error || ""}>
      <AlertCircle size={12} /> ERREUR
    </span>;
  }
  if (status === "done") {
    return <span className="flex items-center gap-2 text-success label-mono text-[10px]">
      <CheckCircle2 size={12} /> {validated ? "VALIDÉ" : "EXTRAIT"}
    </span>;
  }
  return null;
}

function ExtractedDataDialog({ doc, projectId, onClose, onSaved }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (doc?.extracted_data) setData(JSON.parse(JSON.stringify(doc.extracted_data)));
  }, [doc]);

  if (!doc) return null;

  const setField = (path) => (e) => {
    const v = e.target.value === "" ? null : isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value);
    setData((d) => {
      const next = { ...d };
      let cur = next;
      for (let i = 0; i < path.length - 1; i++) {
        cur[path[i]] = { ...(cur[path[i]] || {}) };
        cur = cur[path[i]];
      }
      cur[path[path.length - 1]] = v;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await documentsApi.updateData(projectId, doc.id, data);
      toast.success("Données validées");
      onSaved();
      onClose();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const macro = data.macro_indicators || {};
  const debt = data.debt_data || {};
  const social = data.social_indicators || {};

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="doc-data-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">{doc.filename}</DialogTitle>
          <DialogDescription>
            Type : <strong>{data.document_type}</strong> — {data.document_type_label}
            <br />Institution : {data.institution || "—"} · Pays : {data.country || "—"} · Année : {data.year || "—"}
            <br />Confiance d'extraction : <strong>{data.extraction_confidence}%</strong>
          </DialogDescription>
        </DialogHeader>

        <Section title="INDICATEURS MACRO">
          {Object.entries(macro).map(([k, v]) => (
            <NumField key={k} label={k} value={v ?? ""} onChange={setField(["macro_indicators", k])} />
          ))}
        </Section>
        <Section title="DETTE">
          {Object.entries(debt).map(([k, v]) => (
            <NumField key={k} label={k} value={v ?? ""} onChange={setField(["debt_data", k])} />
          ))}
        </Section>
        <Section title="SOCIAL">
          {Object.entries(social).map(([k, v]) => (
            <NumField key={k} label={k} value={v ?? ""} onChange={setField(["social_indicators", k])} />
          ))}
        </Section>

        {data.loan_clauses?.length > 0 && (
          <div className="mt-4">
            <div className="label-mono text-[9px] text-muted-foreground mb-2">CLAUSES DE PRÊT ({data.loan_clauses.length})</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {data.loan_clauses.map((c, i) => (
                <div key={i} className="border border-border p-3 rounded-sm text-xs">
                  <div className="flex justify-between"><strong>{c.clause_id || `#${i}`}</strong> <span>{c.clause_type}</span></div>
                  <div className="text-muted-foreground">{c.creditor} — {c.amount} {c.currency} — {c.interest_rate}%</div>
                  <div className="mt-1 italic text-[11px]">{c.clause_text_excerpt}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={save} disabled={saving} className="bg-marine hover:bg-marine/90 text-white"
                  data-testid="doc-data-save">
            {saving ? "Enregistrement…" : "Valider les données"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-4">
      <div className="label-mono text-[9px] text-muted-foreground mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      <Input type="number" step="any" value={value ?? ""} onChange={onChange} className="h-8 font-mono text-xs mt-0.5" />
    </div>
  );
}
